// 2) Third-party library imports
import { get, isEqual, isUndefined, uniqWith } from 'lodash-es';
import {
	createSlice,
	createAction,
	PayloadAction,
	createSelector,
} from '@reduxjs/toolkit';
import { App } from 'antd';

// 3a) Local imports � Constants
import {
	ORGANISATION_COMPANIES_PAGE,
	ORGANISATION_USERS_PAGE,
} from '../../config/tableAndPageConstants';
import {
	companiesSortByOptions,
	organisationUsersSortByOptions,
} from '../../constants/organisationsSortAndFilters';

// 3c) Local imports � Project-specific modules / types
import {
	GetOrganisationCompaniesRequestPayload,
	GetOrganisationUsersRequestPayload,
	Organisation,
	OrganisationActionTab,
	OrganisationsState,
	OrganisationUser,
} from './types';
import { Company } from '../companies/types';
import { PageData } from '../common/types';

// 3d) Local imports � Utility functions / interfaces
import { DynamicObject, ResponseModalObject } from '@utils/commonInterfaces';
import { getRegionConfigFromList } from 'src/utils/commonFunctions';

// 3e) Local imports � Redux actions & store types
import { getRegionKeyConfig, getRegionSettingsConfig } from '../auth/authSlice';
import type { RootState } from '../storeTypes';

const actionPrefix = 'organisation';

export const initialState: OrganisationsState = {
    loading: false,
    userOrganisations: [],
    companies: {
        loading: false,
        errorMessages: [],
        data: [],
        pageData: {
            pageSize: ORGANISATION_COMPANIES_PAGE.pageSize,
            currentPage: 0,
            hasNextPage: false,
        },
        filters: {},
        sortBy: companiesSortByOptions[0].value,
        sortAscending: false,
        tableFilter: undefined,
    },
    userCompanies: {
        loading: false,
        data: [],
    },
    users: {
        loading: false,
        errorMessages: [],
        data: [],
        pageData: {
            pageSize: ORGANISATION_USERS_PAGE.pageSize,
            currentPage: 0,
            hasNextPage: false,
        },
        filters: {},
        sortBy: organisationUsersSortByOptions[0].value,
        sortAscending: false,
        tableFilter: undefined,
        editUserLoading: false,
        activeData: {
            loading: false,
            record: {},
            selectedId: null,
            errorMessages: [],
        },
    },
    selectedUserOrganisation: undefined,
    saveLoading: false,
    activeActionTab: undefined,
    historyActionTab: undefined
};

const organisationSlice = createSlice({
	name: actionPrefix,
	initialState,
	reducers: {
		getUserOrganisationsRequestAction: (state) => {
			state.loading = true;
		},
		getUserOrganisationsSuccessAction: (
			state,
			action: PayloadAction<Organisation[]>
		) => {
			state.loading = false;
			state.userOrganisations = action.payload;
		},
		selectUserOrganisationRequestAction: {
			reducer: (
				state,
				_action: PayloadAction<{
					organisationObject: Organisation,
					callback?: () => void,
					modal?: ReturnType<typeof App.useApp>['modal'],
					
				}>
			) => {
				state.loading = true;
			},
			prepare: (
				organisationObject: Organisation,
				callback?: () => void,
				modal?: ReturnType<typeof App.useApp>['modal'],
			) => ({
				payload: {
					organisationObject,
					callback,
					modal
				}
			})
		},
		selectUserOrganisationSuccessAction: (
			state,
			action: PayloadAction<Organisation>
		) => {
			state.loading = false;
			state.selectedUserOrganisation = action.payload;
		},
		clearOrganisationCompaniesStateAllTableFiltersAction: (state) => {
			state.companies.filters = initialState.companies.filters;
			state.companies.sortBy = initialState.companies.sortBy;
			state.companies.sortAscending = initialState.companies.sortAscending;
			state.companies.tableFilter = initialState.companies.tableFilter;
		},
		getOrganisationCompaniesRequestAction: (
			state,
			_action: PayloadAction<DynamicObject | undefined>
		) => {
			state.companies.loading = true;
		},
		getOrganisationCompaniesSuccessAction: (
			state,
			action: PayloadAction<{
				data: Company[],
				pageData?: PageData
			}>
		) => {
			const { data, pageData } = action.payload;

			if (!pageData || pageData.currentPage === 0) {
				state.companies.data = data
			} else {
				state.companies.data = uniqWith(
					[...state.companies.data, ...data],
					isEqual
				);
			}

			if (!isUndefined(pageData)) {
				state.companies.pageData = pageData;
			}

			state.companies.loading = false;
			state.companies.errorMessages = initialState.companies.errorMessages;
		},
		getOrganisationCompaniesErrorAction: (
			state,
			action: PayloadAction<string[]>
		) => {
			state.companies.loading = false;
			state.companies.data = initialState.companies.data;
			state.companies.errorMessages = action.payload;
		},
		getOrganisationCompaniesForUserRequestAction: (state) => {
			state.userCompanies.loading = true;
		},
		getOrganisationCompaniesForUserSuccessAction: (
			state,
			action: PayloadAction<{ data: Company[] }>
		) => {
			const { data } = action.payload;
			state.userCompanies.data = data;
		},
		getOrganisationCompaniesForUserErrorAction: (state) => {
			state.userCompanies.loading = false;
			state.userCompanies.data = initialState.userCompanies.data;
		},
		updateOrganisationCompaniesFiltersAction: (
			state, 
			action: PayloadAction<GetOrganisationCompaniesRequestPayload['filters']>
		) => {
			state.companies.filters = action.payload
		},
		updateOrganisationCompaniesTableFilterAction: (
			state,
			action: PayloadAction<string>
		) => {
			state.companies.tableFilter = action.payload;
		},
		updateOrganisationCompaniesSortByAction: (
			state, 
			action: PayloadAction<{ 
				sortBy: GetOrganisationCompaniesRequestPayload['sortBy'],
				sortAscending: boolean
			}>
		) => {
			const { sortBy, sortAscending } = action.payload;
			state.companies.sortBy = sortBy;
			state.companies.sortAscending = sortAscending;
		},
		saveUserOrganisationRequestAction: {
			reducer: (
				state,
				_action: PayloadAction<{
					payload: any,
					callback?: (response: ResponseModalObject) => void
				}>
			) => {
				state.saveLoading = true;
			},
			prepare: (
				payload: any,
				callback?: (response: ResponseModalObject) => void
			) => ({
				payload: { payload, callback }
			})
		},
		saveUserOrganisationResponseAction: (state) => {
			state.saveLoading = false;
		},
		loadCurrenciesDataRequestAction: {
			reducer: (
				state,
				_action: PayloadAction<{
					fileToUpload: any,
    				callback: (response: ResponseModalObject) => void
				}>
			) => {
				state.saveLoading = true;
			},
			prepare: (
				fileToUpload: any,
    			callback: (response: ResponseModalObject) => void
			) => ({
				payload: { fileToUpload, callback }
			})
		},
		getOrganisationUsersRequestAction: (
			state, 
			_action: PayloadAction<GetOrganisationUsersRequestPayload | undefined>
		) => {
			state.users.loading = true;
		},
		getOrganisationUsersSuccessAction: (
			state,
			action: PayloadAction<{
				data: OrganisationUser[],
				pageData?: PageData
			}>
		) => {
			const { data, pageData } = action.payload;

			if (!pageData || pageData.currentPage === 0) {
				state.users.data = data;
			} else {
				state.users.data = uniqWith(
					[...state.users.data, ...action.payload.data],
					isEqual
				);
			}

			if (!isUndefined(pageData)) {
				state.users.pageData = pageData;
			}

			state.users.loading = false;
			state.users.errorMessages = initialState.users.errorMessages;
		},
		getOrganisationUsersErrorAction: (state, action: PayloadAction<string[]>) => {
			state.users.loading = false;
			state.users.data = initialState.users.data;
			state.users.errorMessages = action.payload;
		},
		updateOrganisationUsersFiltersAction: (
			state, 
			action: PayloadAction<GetOrganisationUsersRequestPayload['filters']>
		) => {
			state.users.filters = action.payload;
		},
		updateOrganisationUsersTableFilterAction: (state, action: PayloadAction<string>) => {
			state.users.tableFilter = action.payload;
		},
		updateOrganisationUsersSortByAction: (
			state,
			action: PayloadAction<{
				sortBy: GetOrganisationUsersRequestPayload['sortBy'],
    			sortAscending: boolean,
			}>
		) => {
			const { sortBy, sortAscending } = action.payload;
			state.users.sortBy = sortBy;
			state.users.sortAscending = sortAscending;
		},
		setSelectedOrganisationUserIdRequestAction: {
			reducer: (
				state,
				_action: PayloadAction<{
					organisationUserId: string | null,
    				callback: () => void
				}>
			) => {
				state.users.activeData.selectedId = initialState.users.activeData.selectedId;
			},
			prepare: (organisationUserId: string | null, callback: () => void) => ({
				payload: {
					organisationUserId,
					callback
				}
			})
		},
		setSelectedOrganisationUserIdSuccessAction: (state, action: PayloadAction<string | null>) => {
			state.users.activeData.selectedId = action.payload;
		},
		getOrganisationUserDataRequestAction: {
			reducer: (
                state,
                _action: PayloadAction<{
                    organisationUserId: string;
                }>
            ) => {
				state.users.activeData.loading = true;
            },
            prepare: (
                organisationUserId: string
            ) => ({
                payload: {
                    organisationUserId
                }
            })
		},
		getOrganisationUserDataSuccessAction: (state, action: PayloadAction<{record: {}}>) => {
			const { record } = action.payload;
			state.users.activeData.record = record;
			state.users.activeData.loading = false;
			state.users.activeData.errorMessages = initialState.users.activeData.errorMessages;
		},
		getOrganisationUserDataErrorAction: (state, action: PayloadAction<string[]>) => {
			state.users.activeData.record = initialState.users.activeData.record;
			state.users.activeData.loading = false;
			state.users.activeData.errorMessages = action.payload;
		},
		clearOrganisationUserDataAction: (state) => {
			state.users.activeData = initialState.users.activeData;
		},
		clearOrganisationActiveTabData: (state) => {
			state.activeActionTab = initialState.activeActionTab;
		},
		setSelectedTabAction: (state, action: PayloadAction<OrganisationActionTab>) => {
			state.activeActionTab = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase('company/selectUserCompanySuccessAction', (state) => {
				state.selectedUserOrganisation = initialState.selectedUserOrganisation;
			})
	}
});

const { actions, reducer } = organisationSlice;

export const {
	getUserOrganisationsRequestAction,
	getUserOrganisationsSuccessAction,
	selectUserOrganisationRequestAction,
	selectUserOrganisationSuccessAction,
	clearOrganisationCompaniesStateAllTableFiltersAction,
	getOrganisationCompaniesRequestAction,
	getOrganisationCompaniesSuccessAction,
	getOrganisationCompaniesErrorAction,
	getOrganisationCompaniesForUserRequestAction,
	getOrganisationCompaniesForUserSuccessAction,
	getOrganisationCompaniesForUserErrorAction,
	updateOrganisationCompaniesFiltersAction,
	updateOrganisationCompaniesTableFilterAction,
	updateOrganisationCompaniesSortByAction,
	saveUserOrganisationRequestAction,
	saveUserOrganisationResponseAction,
	loadCurrenciesDataRequestAction,
	getOrganisationUsersRequestAction,
	getOrganisationUsersSuccessAction,
	getOrganisationUsersErrorAction,
	updateOrganisationUsersFiltersAction,
	updateOrganisationUsersTableFilterAction,
	updateOrganisationUsersSortByAction,
	setSelectedOrganisationUserIdRequestAction,
	setSelectedOrganisationUserIdSuccessAction,
	getOrganisationUserDataRequestAction,
	getOrganisationUserDataSuccessAction,
	getOrganisationUserDataErrorAction,
	clearOrganisationUserDataAction,
	clearOrganisationActiveTabData,
	setSelectedTabAction
} = actions;

export const getOrganisationCustomFieldsFilterListByTypes = createAction(
	`${actionPrefix}/getOrganisationCustomFieldsFilterListByTypes`,
	function prepare(
		customFieldTypes: string[],
    	callback?: (response: DynamicObject) => void
	) {
		return {
			payload: {
				customFieldTypes,
				callback
			}
		};
	}
);
export const closeCompanyRequestAction = createAction(
	`${actionPrefix}/closeCompanyRequestAction`,
	function prepare(payload: DynamicObject) {
		return {
			payload
		};
	}
);
export const removeOrganisationUsersRequestAction = createAction(
	`${actionPrefix}/removeOrganisationUsersRequestAction`,
	function prepare(payload: DynamicObject) {
		return {
			payload
		};
	}
);

// Selectors
export const getOrganisationsState = (state: RootState) => state.organisations;
export const getOrganisationCompanies = createSelector(
	getOrganisationsState, 
	(organisations) => organisations.companies
);
export const getOrganisationCompaniesData = createSelector(
	getOrganisationCompanies, 
	(companies) => companies.data
);
export const getOrganisationUserCompanies = createSelector(
	getOrganisationsState, 
	(organisations) => organisations.userCompanies
);
export const getOrganisationUserCompaniesData = createSelector(
	getOrganisationUserCompanies, 
	(userCompanies) => userCompanies.data
);
export const getUserOrganisations = createSelector(
	getOrganisationsState, 
	(organisations) => organisations.userOrganisations
);
export const getOrganisationUserData = createSelector(
	getOrganisationsState,
	(organisations) => organisations.users.activeData
);
export const getSelectedOrganisationUserId = createSelector(
	getOrganisationUserData,
	(userActiveData) => userActiveData.selectedId
)  
export const getOrganisationsLoading = createSelector(
	getOrganisationsState, 
	(organisations) => organisations.loading
);
export const getSelectedUserOrganisation = createSelector(
	getOrganisationsState, 
	(organisations) => organisations.selectedUserOrganisation
);
export const getSelectedOrganisationId = createSelector(
	getSelectedUserOrganisation,
	(selectedOrganisation) => get(selectedOrganisation, 'OrganisationId')
);
export const getOrganisationActiveActionTab = createSelector(
	getOrganisationsState,
	(organisations) => organisations.activeActionTab
);
export const getOrganisationActionUrl = createSelector(
	[
		getOrganisationActiveActionTab, 
		getRegionKeyConfig, 
		getRegionSettingsConfig
	],
	(
		activeActionTab: OrganisationActionTab | undefined,
		regionKeyConfig: DynamicObject[],
		regionSettingsConfig: DynamicObject[]
	) => {
		if (!activeActionTab) return undefined;
			
		const region = activeActionTab.region;
		let apiUrl: string | undefined = undefined;
	
		if (region) {
	
			const configUsed = getRegionConfigFromList(
				region,
				regionKeyConfig,
				regionSettingsConfig
			);
			apiUrl = get(configUsed, 'Url');
		}
	
		return apiUrl;
	}
);

// Reducer
export default reducer;